import { ILanguage } from '../interfaces/globalization.interfaces';
import { IMessage, ISelectedMessage } from '../interfaces/message.interfaces';
import { ArtifactType, FolderTypeNames, MessageState, MessageType } from '../enums';
import { IFolder } from '../interfaces';

export const formatBytes = (bytes: number): any => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getExtensionFromString = (fileName: string): string => {
    return fileName.substr(fileName.lastIndexOf('.') + 1);
};

export class CheckExp {
    count = 0;
    check = () => {
        ++this.count;
        return new Promise<void>((r, e) => {
            const delay = 1000 * (Math.exp(this.count / 2) - 1);
            if (this.count <= 5) {
                setTimeout(() => {
                    r();
                }, delay);
            } else {
                e();
            }
        });
    };
}

export function clearWhitespaces(inputString: string): string {
    return inputString.replace(/\s/g, '');
}

export function getObjDeepCopy(obj: object) {
    return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

// Returns TRUE if the object is NULL or has no enumarable properties
export function isObjNullOrEmpty(obj: object) {
    return obj === null || (obj && !Object.keys(obj).length);
}

// Converts object's empty strings properties to null
export function convertObjEmptyStringsToNull(obj: any) {
    const newObject = {};
    Object.keys(obj).forEach((key: string | number) => {
        newObject[key] = obj[key] === '' ? null : obj[key];
    });
    return newObject;
}

export function buildQueryParamsStringFromType<T>(type: T): string {
    return Object.keys(type)
        .map((key) => key && `${key}=${type[key]}`)
        .join('&');
}

export function plainTextToHtml(text: string) {
    return text.replace(/(?:\r\n|\r|\n)/g, '<br/>');
}

export function toUpperCaseWithUnderscores(text: string) {
    return text.replace(/([a-zA-Z])(?=[A-Z])/g, '$1_').toUpperCase();
}

export const getCurrentLanguage = (supportedLanguages: ILanguage[], currentLanguageId: number) => {
    return supportedLanguages.find((language) => language.id === currentLanguageId);
};

export function sortListByCurrentLanguage<T>(
    list: Array<T>,
    keySelector: (item: T) => string,
    currentLanguage: ILanguage
): Array<T> {
    if (!Array.isArray(list) || !list.length) return list;

    return list.sort((member1, member2) =>
        keySelector(member1).localeCompare(
            keySelector(member2).toLowerCase(),
            currentLanguage ? currentLanguage.isoCode : 'en'
        )
    );
}

export function checkFormErrors(inputErrors: object): boolean {
    return !Object.keys(inputErrors).some((key) => inputErrors[key]);
}

export function getGlobalCssVariables(names: string[]): string[] {
    const documentStyles = getComputedStyle(document.documentElement);
    return names.map((name) => documentStyles.getPropertyValue(name));
}

export function hexToRgb(hex: string) {
    if (!hex) return '';
    const rgbHex = hex
        .trim()
        .replace('#', '')
        .match(/.{1,2}/g);
    const rgb = {
        r: parseInt(rgbHex[0], 16),
        g: parseInt(rgbHex[1], 16),
        b: parseInt(rgbHex[2], 16),
    };
    return Object.values(rgb).join(',');
}

export function stringToHslColor(str: string) {
    if (!str) {
        return 'hsl(0,0%,0%)';
    }

    let hash = 0;
    const saturation = 50;
    const lightness = 50;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = hash % 360;
    return `hsl(${hue},${saturation}%,${lightness}%)`;
}

export function getSelectedDocuments(documentList: IMessage[], selectedMessages: ISelectedMessage[]): IMessage[] {
    if (!documentList) return [];
    return documentList.filter((document) => {
        return selectedMessages.findIndex((selected) => selected.id === document.id) > -1;
    });
}

export function getLegallyNotifySelectedDocuments(
    documentList: IMessage[],
    selectedMessages: ISelectedMessage[]
): IMessage[] {
    return documentList.filter((document) => {
        return (
            selectedMessages.findIndex((selected) => selected.id === document.id) > -1 &&
            !document.legallyNotified &&
            document.messageType === MessageType.LEGAL_NOTIFICATION
        );
    });
}

export function isLegalNotification(document: IMessage): boolean {
    return document.messageType === MessageType.LEGAL_NOTIFICATION;
}

export function isLegallyNotified(document: IMessage): boolean {
    return document.legallyNotified && document.messageType === MessageType.LEGAL_NOTIFICATION;
}

function isHtmlMimeAvailable(artifact: any) {
    //[]
    if (artifact.files.length == 0) {
        return [];
    }
    //[html, txt]
    if (artifact.files.filter((file: any) => file.encodingFormat == 'text/html').length > 0) {
        return artifact.files.filter((file: any) => file.encodingFormat != 'text/html');
    }
    //[txt, pdf]
    // skip 1st document from mainbodyDocument
    else {
        return artifact.files.length > 1 ? artifact.files.filter((item: any, index: any) => index != 0) : [];
    }
}

export function filterAttachmentsOnly(document: IMessage) {
    const result = document.artifacts
        ? document.artifacts.map((artifact) => {
              return artifact.type === ArtifactType.MESSAGE_BODY_DOCUMENT
                  ? { ...artifact, files: isHtmlMimeAvailable(artifact) }
                  : { ...artifact };
          })
        : [];
    return result.filter((item: any) => item.files.length != 0);
}

export function isMovingAllowed(sourceMessageState: MessageState, destinationFolderType: string) {
    if (sourceMessageState.length > 0) {
        if (
            sourceMessageState === MessageState.RECEIVED &&
            [FolderTypeNames.SENT, FolderTypeNames.DRAFT].includes(destinationFolderType as FolderTypeNames)
        ) {
            return false;
        }
        if (
            sourceMessageState === MessageState.DRAFT &&
            [FolderTypeNames.SENT, FolderTypeNames.INBOX].includes(destinationFolderType as FolderTypeNames)
        ) {
            return false;
        }
        if (
            sourceMessageState === MessageState.SENT &&
            [FolderTypeNames.INBOX, FolderTypeNames.DRAFT].includes(destinationFolderType as FolderTypeNames)
        ) {
            return false;
        }
        return true;
    }
    return false;
}

export function getMovingAllowedTypes(messageState: MessageState): string[] {
    const allowedTypes: FolderTypeNames[] = [];
    const typesToCheck = [
        FolderTypeNames.INBOX,
        FolderTypeNames.DRAFT,
        FolderTypeNames.SENT,
        FolderTypeNames.TRASH,
        FolderTypeNames.CUSTOM,
    ];

    typesToCheck.forEach((type) => {
        if (isMovingAllowed(messageState, type)) {
            allowedTypes.push(type);
        }
    });

    return allowedTypes;
}

export function capitalizeString(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getCrossBrowserPrefixedProp(propName: string, target: object) {
    if (typeof target[propName] !== 'undefined') {
        return propName;
    } else if (typeof target[`moz${capitalizeString(propName)}`] !== 'undefined') {
        return `moz${capitalizeString(propName)}`;
    } else if (typeof target[`ms${capitalizeString(propName)}`] !== 'undefined') {
        return `ms${capitalizeString(propName)}`;
    } else if (typeof target[`webkit${capitalizeString(propName)}`] !== 'undefined') {
        return `webkit${capitalizeString(propName)}`;
    } else {
        return undefined;
    }
}

/**
 * Hide "download" and "information" on documents
 * https://jira.e-boks.dk/browse/FREM-1603
 * Re-enable as part of https://jira.e-boks.dk/browse/FREM-693
 */

// Build the query string of single or comma-separated numbers depending on input type.
export function buildIdsQueryParamString(input: string | Array<string>): string {
    if (Array.isArray(input)) {
        return input.join(',');
    } else {
        return input ? input : '';
    }
}

export const getSenderInfo = (records: any) => {
    const uniqueRecords = [];
    for (const item in records) {
        uniqueRecords.push({
            senderName: records[item].senderName,
            count: 0,
            read: records[item].read,
            organisationId: records[item].senderId,
        });
    }
    // For case-insensitive sorting
    uniqueRecords.length != 0 &&
        uniqueRecords.sort(function (s1, s2) {
            const l = s1.senderName?.toLowerCase(),
                m = s2.senderName?.toLowerCase();
            return l === m ? 0 : l > m ? 1 : -1;
        });

    // Getting unique records from all the senders list with count
    const tempRecords = {};
    for (const { senderName, organisationId } of uniqueRecords) {
        tempRecords[senderName] = {
            senderName,
            organisationId,
            count: records.filter((item: any) => !item.read && item.senderName == senderName).length,
        };
    }
    let result: any = [];
    result = Object.values(tempRecords);

    // Making collection from unique records
    const senderRecord = [];
    for (const item in result) {
        senderRecord.push({
            name: result[item].senderName,
            count: result[item].count,
            id: `sender_id_${item}`,
            isActive: false,
            organisationId: result[item].organisationId,
        });
    }
    return senderRecord;
};

export const updateStorageMailIdFolderId = (key: string, params?: object) => {
    if (key === 'folderTypeOwner') {
        localStorage.getItem('mailboxId') !== null && localStorage.removeItem('mailboxId');
        sessionStorage.getItem('selectedFolderId') !== null && sessionStorage.removeItem('selectedFolderId');
    }

    if (key === 'redirectToFolder') {
        localStorage.setItem('mailboxId', params['mid']);
        sessionStorage.setItem('selectedFolderId', params['fid']);
    }
};

export const closePopupOFocusOut = (event: any, previousStep: boolean) => {
    const p = {};
    Object.assign(p, event.relatedTarget);
    const q: any = Object.values(p)[0];
    if (previousStep) {
        return q?.memoizedProps?.className?.includes('prevFocusedElem');
    } else {
        return !q?.memoizedProps?.className?.includes('prevFocusedElem');
    }
};

export const getfolderHirachy = (
    data: Array<IFolder>,
    parentId: string | null = null,
    level = 0,
    result: string[] = []
): string[] => {
    return data.reduce((acc, item) => {
        if (item.parentId === parentId) {
            const levelItem: any = {
                id: item.id,
                name: ' '.repeat(level) + '-'.repeat(level) + ' ' + item.name,
                parentId: item.parentId,
                mailboxId: item.mailboxId,
                isActive: item.isActive,
                folderType: item.folderType,
                version: item.version,
            };
            acc.push(levelItem);
            acc = acc.concat(getfolderHirachy(data, item.id, level + 1, []));
        }
        return acc;
    }, result);
};
